/**
 * Contact Work outreach, assignment, and letter-lane binders.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError, getDashboardOperator } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import { getContactWorkFilters } from "./dashboard-saved-view-state.js";
import {
  canOverrideContactPolicy,
  contactPolicyDecisionLabel,
  contactPolicyRoleSummary,
  contactPolicySummary,
  defaultPolicyOverrideReason,
  evaluateDashboardContactPolicy
} from "./dashboard-contact-policy.js";
import {
  submitContactLetterPacket,
  submitContactLetterPacketBatch,
  submitContactLetterPacketMailManifest,
  submitContactLetterPacketReturnedMailBatch,
  submitContactLetterPacketSendBatch,
  submitContactMailBatchVendorHandoff,
  downloadAuthenticatedFile,
  printAuthenticatedLetterPacket
} from "./dashboard-letter-packet-actions.js";
import type {
  ContactPolicyUiDecision,
  ContactSpecialtyResult,
  ContactWorkSavedViewFilters
} from "./dashboard-lead-factory-types.js";

const leadFactoryOutreachChannels = ["phone", "letter", "email", "skip_trace", "pi", "note"] as const;
const leadFactoryOutreachOutcomes = [
  "attempted",
  "connected",
  "interested",
  "left_message",
  "sent",
  "returned_mail",
  "bad_contact",
  "needs_follow_up",
  "not_interested",
  "do_not_contact",
  "pi_needed",
  "skip_trace_needed"
] as const;
const skipTraceResultStatuses = ["phone_found", "email_found", "mailing_address_found", "no_contact_found", "owner_deceased", "needs_pi_review"] as const;
const piResultStatuses = ["owner_located", "death_confirmed", "heir_identified", "probate_found", "no_owner_contact", "needs_skip_trace"] as const;

function datetimeLocalToIso(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

export async function submitRetryLetterAddressVerification(leadId: string, contactMethodId: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  statusElement.textContent = `${leadId}: verifying retry-letter mailing address...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/contact-remediation`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        contactMethodIdsToVerify: [contactMethodId],
        note: "Verified returned-mail retry-letter mailing address."
      })
    });
    const body = (await response.json()) as {
      error?: string;
      verifiedContactIds?: string[];
      contactReadiness?: string;
      status?: string;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: verified ${formatInteger(body.verifiedContactIds?.length ?? 0)} retry-letter address; readiness ${labelize(body.contactReadiness || body.status || "updated")}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Retry-letter address verification failed.";
  }
}

export function bindContactOutreachActions(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLButtonElement>("[data-contact-work-bulk-assign]").forEach((button) => {
    button.addEventListener("click", () => {
      const taskIds = (button.dataset.contactWorkBulkAssign || "").split(",").map((value) => value.trim()).filter(Boolean);
      const panel = button.closest<HTMLElement>(".panel");
      const assignedTo =
        panel?.querySelector<HTMLInputElement>("[data-contact-work-bulk-assignee]")?.value.trim() || getDashboardOperator() || "";
      const label = button.dataset.contactWorkBulkLabel || "Contact Work";
      void submitContactWorkBulkAssign(taskIds, assignedTo, label, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-work-filter-assign]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>(".panel");
      const assignedTo =
        panel?.querySelector<HTMLInputElement>("[data-contact-work-bulk-assignee]")?.value.trim() || getDashboardOperator() || "";
      const label = button.dataset.contactWorkBulkLabel || "full filtered Contact Work";
      const limit = Number(button.dataset.contactWorkFilterLimit || "1000");
      void submitContactWorkFilterAssign(assignedTo, label, limit, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-work-plan-assign]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>(".panel");
      const assignedTo =
        panel?.querySelector<HTMLInputElement>("[data-contact-work-bulk-assignee]")?.value.trim() || getDashboardOperator() || "";
      const label = button.dataset.contactWorkBulkLabel || "Contact Work Plan lane";
      const limit = Number(button.dataset.contactWorkPlanLimit || "1000");
      const laneFilters = contactWorkPlanAssignmentFilters(button);
      void submitContactWorkFilterAssign(assignedTo, label, limit, statusElement, actions, laneFilters);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-work-operator-assign]").forEach((button) => {
    button.addEventListener("click", () => {
      const assignedTo = button.dataset.contactWorkOperatorAssignee?.trim() || "";
      const label = button.dataset.contactWorkBulkLabel || "Contact Work operator target";
      const limit = Number(button.dataset.contactWorkPlanLimit || "0");
      const laneFilters = contactWorkPlanAssignmentFilters(button);
      void submitContactWorkFilterAssign(assignedTo, label, limit, statusElement, actions, laneFilters);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-work-operator-rebalance]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>(".panel");
      const toAssignedTo =
        panel?.querySelector<HTMLInputElement>("[data-contact-work-bulk-assignee]")?.value.trim() || getDashboardOperator() || "";
      const fromAssignedTo = button.dataset.contactWorkRebalanceFrom?.trim() || "";
      const limit = Number(button.dataset.contactWorkRebalanceLimit || "0");
      const label = button.dataset.contactWorkBulkLabel || "Contact Work rebalance";
      void submitContactWorkRebalance(fromAssignedTo, toAssignedTo, label, limit, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-letter-batch-generate]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>("[data-contact-letter-batch-panel]");
      const lane = button.closest<HTMLElement>(".contact-lane");
      const senderName = panel?.querySelector<HTMLInputElement>("[data-letter-batch-sender]")?.value.trim() || "";
      const note = panel?.querySelector<HTMLInputElement>("[data-letter-batch-note]")?.value.trim() || "Generated visible Contact Work mail batch.";
      const tasks = Array.from(lane?.querySelectorAll<HTMLFormElement>("[data-contact-letter-packet-form]") ?? [])
        .map((form) => ({
          propertyLeadId: form.dataset.leadId?.trim() || "",
          taskId: form.dataset.contactTaskId?.trim() || ""
        }))
        .filter((task) => task.propertyLeadId && task.taskId);
      void submitContactLetterPacketBatch(tasks, { senderName, note }, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-letter-batch-manifest]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>("[data-contact-letter-batch-panel]");
      const lane = button.closest<HTMLElement>(".contact-lane");
      const note = panel?.querySelector<HTMLInputElement>("[data-letter-batch-note]")?.value.trim() || "Created visible Contact Work mail manifest.";
      const packetIds = Array.from(lane?.querySelectorAll<HTMLElement>("[data-contact-letter-packet-row]") ?? [])
        .filter((row) => row.dataset.letterPacketStatus === "ready_to_print")
        .map((row) => row.dataset.letterPacketId?.trim() || "")
        .filter(Boolean);
      void submitContactLetterPacketMailManifest(packetIds, { note }, statusElement, panel ?? undefined, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-letter-batch-handoff]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>("[data-contact-letter-batch-panel]");
      const batchId = panel?.dataset.contactMailBatchId?.trim() || "";
      const note = panel?.querySelector<HTMLInputElement>("[data-letter-batch-note]")?.value.trim() || "Created Contact Work vendor handoff.";
      void submitContactMailBatchVendorHandoff(batchId, { handoffType: "postal_runner", vendorName: "Lead Factory postal runner", note }, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-letter-batch-send]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>("[data-contact-letter-batch-panel]");
      const lane = button.closest<HTMLElement>(".contact-lane");
      const note = panel?.querySelector<HTMLInputElement>("[data-letter-batch-note]")?.value.trim() || "Recorded visible Contact Work mail batch as sent.";
      const packetIds = Array.from(lane?.querySelectorAll<HTMLElement>("[data-contact-letter-packet-row]") ?? [])
        .filter((row) => row.dataset.letterPacketStatus === "ready_to_print")
        .map((row) => row.dataset.letterPacketId?.trim() || "")
        .filter(Boolean);
      void submitContactLetterPacketSendBatch(packetIds, { note }, statusElement, lane ?? undefined, panel ?? undefined, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-contact-letter-batch-returned]").forEach((button) => {
    button.addEventListener("click", () => {
      const panel = button.closest<HTMLElement>("[data-contact-letter-batch-panel]");
      const lane = button.closest<HTMLElement>(".contact-lane");
      const note = panel?.querySelector<HTMLInputElement>("[data-letter-batch-note]")?.value.trim() || "Recorded visible returned mail batch.";
      const packetIds = Array.from(lane?.querySelectorAll<HTMLElement>("[data-contact-letter-packet-row]") ?? [])
        .filter((row) => row.dataset.letterPacketStatus === "sent")
        .map((row) => row.dataset.letterPacketId?.trim() || "")
        .filter(Boolean);
      void submitContactLetterPacketReturnedMailBatch(packetIds, { note }, statusElement, lane ?? undefined, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-letter-packet-download]").forEach((button) => {
    button.addEventListener("click", () => {
      const packetId = button.dataset.letterPacketDownload?.trim() || "letter-packet";
      const url = button.dataset.letterPacketUrl?.trim();
      if (!url) {
        statusElement.textContent = "Letter packet download cancelled; no artifact URL is linked.";
        return;
      }
      void downloadAuthenticatedFile(url, `${packetId}.md`, "text/markdown", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-returned-mail-batch-download]").forEach((button) => {
    button.addEventListener("click", () => {
      const batchId = button.dataset.returnedMailBatchDownload?.trim() || "returned-mail-batch";
      const url = button.dataset.returnedMailBatchUrl?.trim();
      if (!url) {
        statusElement.textContent = "Returned-mail batch download cancelled; no artifact URL is linked.";
        return;
      }
      void downloadAuthenticatedFile(url, `${batchId}.md`, "text/markdown", statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-letter-packet-print]").forEach((button) => {
    button.addEventListener("click", () => {
      const packetId = button.dataset.letterPacketPrint?.trim() || "letter-packet";
      const url = button.dataset.letterPacketUrl?.trim();
      if (!url) {
        statusElement.textContent = "Letter packet print cancelled; no artifact URL is linked.";
        return;
      }
      void printAuthenticatedLetterPacket(url, packetId, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-contact-letter-packet-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId?.trim() || "";
      const taskId = form.dataset.contactTaskId?.trim() || "";
      if (!leadId || !taskId) {
        statusElement.textContent = "Letter packet generation cancelled; no lead/task is linked.";
        return;
      }
      const senderName = form.querySelector<HTMLInputElement>("[data-letter-packet-sender]")?.value.trim() || "";
      const note = form.querySelector<HTMLInputElement>("[data-letter-packet-note]")?.value.trim() || "Generated printable owner letter packet from CRM evidence.";
      void submitContactLetterPacket(leadId, taskId, { senderName, note }, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-contact-outreach-form]").forEach((form) => {
    if (form.dataset.outreachBound === "1") return;
    form.dataset.outreachBound = "1";
    bindContactPolicyReviewActions(form);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const leadId = form.dataset.leadId;
      if (!leadId) return;
      const channel = form.querySelector<HTMLSelectElement>("[data-contact-outreach-channel]")?.value.trim() || "letter";
      const outcome = form.querySelector<HTMLSelectElement>("[data-contact-outreach-outcome]")?.value.trim() || "sent";
      const followUpValue = form.querySelector<HTMLInputElement>("[data-contact-outreach-follow-up]")?.value.trim() || "";
      const policyOverrideReason = form.querySelector<HTMLInputElement>("[data-contact-outreach-policy]")?.value.trim() || "";
      const note = form.querySelector<HTMLInputElement>("[data-contact-outreach-note]")?.value.trim() || "Dashboard recorded structured outreach.";
      const completeTaskId = form.dataset.contactTaskId?.trim() || null;
      const specialtyResult = specialtyResultFromForm(form, statusElement);
      if (specialtyResult === false) return;
      const policyDecision = evaluateDashboardContactPolicy(
        channel,
        outcome,
        form.dataset.leadStatus || "",
        form.dataset.contactReadiness || "",
        new Date()
      );
      if (!contactPolicyOverrideReady(form, policyDecision, policyOverrideReason, statusElement)) return;
      void submitStructuredOutreachAttempt(
        leadId,
        {
          channel,
          outcome,
          followUpAt: followUpValue ? datetimeLocalToIso(followUpValue) : null,
          policyOverrideReason,
          note,
          completeTaskId,
          specialtyResult
        },
        statusElement,
        actions
      );
    });
  });
}

export async function submitContactWorkBulkAssign(taskIds: string[], assignedTo: string, label: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  if (taskIds.length === 0) {
    statusElement.textContent = `${label}: no ready Contact Work tasks selected for assignment.`;
    return;
  }
  if (!assignedTo.trim()) {
    statusElement.textContent = `${label}: assignment cancelled; enter an assignee.`;
    return;
  }
  statusElement.textContent = `${label}: assigning ${formatInteger(taskIds.length)} Contact Work tasks...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/contact-work-queue/assign", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        taskIds,
        assignedTo: assignedTo.trim(),
        note: `Bulk assigned ${label} from Contact Work dashboard.`
      })
    });
    const body = (await response.json()) as { error?: string; assignedCount?: number; skippedCount?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${label}: assigned ${formatInteger(body.assignedCount ?? 0)} Contact Work tasks to ${assignedTo.trim()}${body.skippedCount ? `; skipped ${formatInteger(body.skippedCount)}` : ""}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Contact Work assignment failed.";
  }
}

export function contactWorkPlanAssignmentFilters(button: HTMLButtonElement): ContactWorkSavedViewFilters {
  const filters = getContactWorkFilters();
  const queue = button.dataset.contactWorkPlanQueue || filters.queue || "all";
  const letterKind = button.dataset.contactWorkPlanLetterKind || "all";
  return {
    ...filters,
    queue,
    letterKind,
    assignedTo: "",
    leadId: ""
  };
}

export async function submitContactWorkFilterAssign(
  assignedTo: string,
  label: string,
  limit: number,
  statusElement: HTMLElement,
  actions: DashboardActionHost,
  filtersOverride?: ContactWorkSavedViewFilters
): Promise<void> {
  if (!assignedTo.trim()) {
    statusElement.textContent = `${label}: assignment cancelled; enter an assignee.`;
    return;
  }
  const filters = filtersOverride ?? getContactWorkFilters();
  statusElement.textContent = `${label}: assigning matching Contact Work tasks...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/contact-work-capacity/assign", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        assignedTo: assignedTo.trim(),
        filters,
        limit,
        note: `Assigned ${label} from Contact Work dashboard filters.`
      })
    });
    const body = (await response.json()) as { error?: string; assignedCount?: number; assignmentLimit?: number; capped?: boolean };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${label}: assigned ${formatInteger(body.assignedCount ?? 0)} Contact Work tasks to ${assignedTo.trim()}${body.capped ? `; capped at ${formatInteger(body.assignmentLimit ?? limit)}` : ""}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Filtered Contact Work assignment failed.";
  }
}

export async function submitContactWorkRebalance(
  fromAssignedTo: string,
  toAssignedTo: string,
  label: string,
  limit: number,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  if (!fromAssignedTo.trim()) {
    statusElement.textContent = `${label}: rebalance cancelled; no source operator is linked.`;
    return;
  }
  if (!toAssignedTo.trim()) {
    statusElement.textContent = `${label}: rebalance cancelled; enter a target assignee.`;
    return;
  }
  if (fromAssignedTo !== "unassigned" && fromAssignedTo === toAssignedTo.trim()) {
    statusElement.textContent = `${label}: rebalance cancelled; choose a different target assignee.`;
    return;
  }
  const assignmentLimit = Math.max(1, Math.floor(limit || 1));
  statusElement.textContent = `${label}: rebalancing up to ${formatInteger(assignmentLimit)} Contact Work tasks...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/contact-work-capacity/rebalance", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        fromAssignedTo,
        toAssignedTo: toAssignedTo.trim(),
        filters: getContactWorkFilters(),
        limit: assignmentLimit,
        note: `Rebalanced ${label} from Contact Work operator accountability.`
      })
    });
    const body = (await response.json()) as { error?: string; rebalancedCount?: number; assignmentLimit?: number; capped?: boolean };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${label}: rebalanced ${formatInteger(body.rebalancedCount ?? 0)} Contact Work tasks to ${toAssignedTo.trim()}${body.capped ? `; capped at ${formatInteger(body.assignmentLimit ?? assignmentLimit)}` : ""}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Contact Work rebalance failed.";
  }
}

export function specialtyResultFromForm(form: HTMLFormElement, statusElement: HTMLElement): ContactSpecialtyResult | null | false {
  const capture = form.querySelector<HTMLElement>("[data-specialty-result-capture]");
  if (!capture) return null;
  const resultType = capture.dataset.specialtyResultType || "";
  const resultStatus = capture.querySelector<HTMLSelectElement>("[data-specialty-result-status]")?.value.trim() || "";
  const confidenceValue = capture.querySelector<HTMLInputElement>("[data-specialty-result-confidence]")?.value.trim() || "";
  const confidence = Number(confidenceValue);
  const allowedStatuses = resultType === "skip_trace" ? skipTraceResultStatuses : resultType === "pi_research" ? piResultStatuses : [];
  if (!allowedStatuses.includes(resultStatus as never)) {
    statusElement.textContent = `Specialty result cancelled; choose a valid ${labelize(resultType || "contact work")} result.`;
    return false;
  }
  if (!Number.isFinite(confidence) || confidence < 0 || confidence > 1) {
    statusElement.textContent = "Specialty result cancelled; confidence must be between 0 and 1.";
    return false;
  }
  return {
    resultType,
    resultStatus,
    source: capture.querySelector<HTMLInputElement>("[data-specialty-result-source]")?.value.trim() || null,
    phone: capture.querySelector<HTMLInputElement>("[data-specialty-result-phone]")?.value.trim() || null,
    email: capture.querySelector<HTMLInputElement>("[data-specialty-result-email]")?.value.trim() || null,
    address: capture.querySelector<HTMLInputElement>("[data-specialty-result-address]")?.value.trim() || null,
    contactName: capture.querySelector<HTMLInputElement>("[data-specialty-result-contact-name]")?.value.trim() || null,
    finding: capture.querySelector<HTMLInputElement>("[data-specialty-result-finding]")?.value.trim() || null,
    evidence: capture.querySelector<HTMLInputElement>("[data-specialty-result-evidence]")?.value.trim() || null,
    confidence
  };
}

export function bindContactPolicyReviewActions(form: HTMLFormElement): void {
  const update = () => {
    const channel = form.querySelector<HTMLSelectElement>("[data-contact-outreach-channel]")?.value.trim() || "letter";
    const outcome = form.querySelector<HTMLSelectElement>("[data-contact-outreach-outcome]")?.value.trim() || "sent";
    const decision = evaluateDashboardContactPolicy(
      channel,
      outcome,
      form.dataset.leadStatus || "",
      form.dataset.contactReadiness || "",
      new Date()
    );
    const panel = form.querySelector<HTMLElement>("[data-contact-policy-review]");
    if (!panel) return;
    panel.classList.toggle("allowed", decision.allowed);
    panel.classList.toggle("blocked", !decision.allowed);
    const label = panel.querySelector<HTMLElement>("[data-contact-policy-decision]");
    const summary = panel.querySelector<HTMLElement>("[data-contact-policy-summary]");
    const role = panel.querySelector<HTMLElement>("[data-contact-policy-role]");
    const ackLabel = panel.querySelector<HTMLElement>("[data-contact-policy-ack-label]");
    const ack = panel.querySelector<HTMLInputElement>("[data-contact-policy-ack]");
    const reason = panel.querySelector<HTMLInputElement>("[data-contact-policy-reason]");
    if (label) label.textContent = contactPolicyDecisionLabel(decision);
    if (summary) summary.textContent = contactPolicySummary(decision);
    if (role) role.textContent = contactPolicyRoleSummary(decision);
    if (ackLabel) ackLabel.classList.toggle("is-hidden", decision.allowed);
    if (ack) {
      ack.disabled = decision.allowed || !canOverrideContactPolicy();
      if (decision.allowed) ack.checked = false;
    }
    if (reason && !decision.allowed && !reason.value.trim()) reason.value = defaultPolicyOverrideReason(decision.reason);
  };
  form.querySelectorAll<HTMLSelectElement>("[data-contact-outreach-channel], [data-contact-outreach-outcome]").forEach((control) => {
    control.addEventListener("change", update);
  });
  update();
}

export function contactPolicyOverrideReady(
  form: HTMLFormElement,
  decision: ContactPolicyUiDecision,
  policyOverrideReason: string,
  statusElement: HTMLElement
): boolean {
  if (decision.allowed) return true;
  if (!policyOverrideReason) {
    statusElement.textContent = `Outreach blocked by policy: ${labelize(decision.reason)}. Add an override reason.`;
    return false;
  }
  if (!canOverrideContactPolicy()) {
    statusElement.textContent = `Outreach blocked by policy: ${labelize(decision.reason)}. Manager, admin, or automation role required.`;
    return false;
  }
  const acknowledged = form.querySelector<HTMLInputElement>("[data-contact-policy-ack]")?.checked ?? false;
  if (!acknowledged) {
    statusElement.textContent = `Outreach blocked by policy: ${labelize(decision.reason)}. Manager override approval is required.`;
    return false;
  }
  return true;
}

export async function submitOutreachAttempt(
  leadId: string,
  channel: string,
  outcome: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  await submitStructuredOutreachAttempt(
    leadId,
    {
      channel,
      outcome,
      note: `Dashboard recorded ${channel} outreach.`
    },
    statusElement,
    actions,
    true
  );
}

export async function submitStructuredOutreachAttempt(
  leadId: string,
  payload: {
    channel: string;
    outcome: string;
    note: string;
    followUpAt?: string | null;
    policyOverrideReason?: string;
    completeTaskId?: string | null;
    specialtyResult?: ContactSpecialtyResult | null;
  },
  statusElement: HTMLElement,
  actions: DashboardActionHost,
  refreshAfterSuccess = false
): Promise<void> {
  if (!leadFactoryOutreachChannels.includes(payload.channel as (typeof leadFactoryOutreachChannels)[number])) {
    statusElement.textContent = `Outreach cancelled; choose one of ${leadFactoryOutreachChannels.join(", ")}.`;
    return;
  }
  if (!leadFactoryOutreachOutcomes.includes(payload.outcome as (typeof leadFactoryOutreachOutcomes)[number])) {
    statusElement.textContent = `Outreach cancelled; choose one of ${leadFactoryOutreachOutcomes.join(", ")}.`;
    return;
  }
  if (payload.outcome === "needs_follow_up" && !payload.followUpAt) {
    statusElement.textContent = "Outreach cancelled; follow-up date is required for needs follow up.";
    return;
  }
  statusElement.textContent = `Recording ${labelize(payload.channel)} outreach for ${leadId}...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/outreach`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as {
      error?: string;
      status?: string;
      contactReadiness?: string | null;
      specialtyContactUpdate?: {
        contactIds?: string[];
        status?: string;
        contactReadiness?: string;
        nextContactActions?: Array<{ taskType?: string; task_type?: string }>;
      } | null;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const contactCount = body.specialtyContactUpdate?.contactIds?.length ?? 0;
    const readiness = body.specialtyContactUpdate?.contactReadiness ?? body.contactReadiness ?? null;
    const contactEvidenceText = contactCount === 1 ? "contact added; readiness" : "contacts added; readiness";
    const readinessText = contactCount > 0 && readiness
      ? ` ${formatInteger(contactCount)} ${contactEvidenceText} ${labelize(readiness)}.`
      : "";
    const nextActions = body.specialtyContactUpdate?.nextContactActions ?? [];
    const nextActionLabels = nextActions
      .map((action) => action.taskType ?? action.task_type ?? "")
      .filter(Boolean)
      .map(labelize);
    const nextActionsText = nextActionLabels.length > 0 ? ` Next actions ready: ${nextActionLabels.join(", ")}.` : "";
    statusElement.textContent = `${leadId}: outreach recorded.${readinessText}${nextActionsText}`;
    if (refreshAfterSuccess) {
      statusElement.textContent = `${leadId}: outreach recorded.${readinessText}${nextActionsText} Refreshing...`;
      setTimeout(() => actions.refresh(), 650);
    }
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Outreach action failed.";
  }
}

